import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Input, Label, Spinner, Table, Button } from "reactstrap";
import { Link } from "react-router-dom";
import {
  API_TIMEOUT,
  BELANJA_MAMIN_PD_REKAP,
  BELANJA_MAMIN_PD_TAHUN,
} from "../../utils";
import s from "./belanjaMamin.module.scss";

const formatRupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const BelanjaMaminRekapPage = () => {
  const currentYear = new Date().getFullYear();
  const [tahun, setTahun] = useState(String(currentYear));
  const [rows, setRows] = useState([]);
  const [rekap, setRekap] = useState(null);
  const [loading, setLoading] = useState(false);

  const listTahun = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= 2018; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  useEffect(() => {
    if (!tahun) return;
    setLoading(true);
    Promise.all([
      axios.get(BELANJA_MAMIN_PD_TAHUN + tahun, { timeout: API_TIMEOUT }),
      axios.get(BELANJA_MAMIN_PD_REKAP, {
        timeout: API_TIMEOUT,
        params: { tahun },
      }),
    ])
      .then(([resList, resRekap]) => {
        setRows(resList.data?.data || []);
        setRekap(resRekap.data?.data || null);
      })
      .catch(() => {
        setRows([]);
        setRekap(null);
      })
      .finally(() => setLoading(false));
  }, [tahun]);

  const totalKeseluruhan = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.total_belanja_mamin) || 0), 0),
    [rows]
  );

  return (
    <div className={`animated fadeIn ${s.pageWrap}`}>
      <div className={s.heroSection}>
        <h2>Belanja Makan Minum</h2>
        <p>
          Rekap total belanja makan-minum per perangkat daerah berdasarkan tahun
          anggaran.
        </p>
      </div>

      <div className={s.sectionCard}>
        <div className={s.cardHeader}>Filter &amp; ringkasan</div>
        <div className={s.cardBody}>
          <div className={s.toolbar}>
            <div>
              <Label for="tahun-rekap">Tahun anggaran</Label>
              <Input
                id="tahun-rekap"
                type="select"
                value={tahun}
                onChange={(e) => setTahun(e.target.value)}
                style={{ minWidth: 140 }}
              >
                {listTahun.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Input>
            </div>
            <Button tag={Link} to="/admin/makan-minum/belanja-pd" color="primary">
              Input / Upload Belanja PD
            </Button>
          </div>

          <div className={s.summaryRow}>
            <div className={s.summaryCard}>
              <div className={s.label}>Jumlah PD</div>
              <div className={s.value}>
                {rekap?.jumlah_pd ?? rows.length}
              </div>
            </div>
            <div className={s.summaryCard}>
              <div className={s.label}>Total belanja {tahun}</div>
              <div className={s.value}>
                {formatRupiah(rekap?.total_keseluruhan ?? totalKeseluruhan)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={s.sectionCard}>
        <div className={s.cardHeader}>
          Daftar perangkat daerah — tahun {tahun}
        </div>
        <div className={`${s.cardBody} ${s.tableWrap}`}>
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted mb-0">
              Belum ada data belanja makan-minum untuk tahun ini.{" "}
              <Link to="/admin/makan-minum/belanja-pd">Tambah data</Link>
            </p>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Perangkat daerah</th>
                  <th className="text-end">Total belanja mamin</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td>{idx + 1}</td>
                    <td>{row.nama_dinas}</td>
                    <td className="text-end fw-semibold">
                      {formatRupiah(row.total_belanja_mamin)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} className="text-end fw-bold">
                    Total keseluruhan
                  </td>
                  <td className="text-end fw-bold">
                    {formatRupiah(totalKeseluruhan)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default BelanjaMaminRekapPage;
